import React, { useState } from 'react';
import axios from 'axios';
import { toLocalDateString, parseLocalDate, getTodayLocal } from '../utils/dateUtils';

interface VisitScheduleDay {
  date: Date;
  isVisitDay: boolean;
  notes?: string;
  time?: string;
}

interface CustomerVisitCalendarProps {
  jobId: string;
  startDate: Date;
  endDate: Date;
  visitSchedule: VisitScheduleDay[];
  specialInstructions?: string;
  caregiverId: string;
  onScheduleUpdate?: () => void;
  currentPreferredTime?: string;
}

const CustomerVisitCalendar: React.FC<CustomerVisitCalendarProps> = ({
  jobId,
  startDate,
  endDate,
  visitSchedule,
  specialInstructions,
  caregiverId,
  onScheduleUpdate,
  currentPreferredTime
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    const start = parseLocalDate(startDate);
    return now >= start ? now : start;
  });
  
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState<string>('');
  const [dayStartTime, setDayStartTime] = useState<string>('');
  const [dayEndTime, setDayEndTime] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [editingTime, setEditingTime] = useState(false);
  const [headerStartTime, setHeaderStartTime] = useState('');
  const [headerEndTime, setHeaderEndTime] = useState('');
  const [updateAllDays, setUpdateAllDays] = useState(false);
  const [timeError, setTimeError] = useState('');

  const validateTimeDuration = (start: string, end: string): boolean => {
    if (!start || !end) {
      setTimeError('Please select both start and end times');
      return false;
    }

    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    if (endMinutes <= startMinutes) {
      setTimeError('End time must be after start time');
      return false;
    }
    
    const durationMinutes = endMinutes - startMinutes;
    const durationHours = durationMinutes / 60;
    
    if (durationHours > 8) {
      setTimeError('Visit duration cannot exceed 8 hours');
      return false;
    }
    
    setTimeError('');
    return true;
  };

  const formatTimeRange = (start: string, end: string): string => {
    const formatTime = (time: string) => {
      const [hour, min] = time.split(':').map(Number);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const parseTimeRange = (timeRange: string): { start: string; end: string } | null => {
    const match = timeRange.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return null;
    
    let startHour = parseInt(match[1]);
    const startMin = match[2];
    const startPeriod = match[3];
    let endHour = parseInt(match[4]);
    const endMin = match[5];
    const endPeriod = match[6];
    
    if (startPeriod) {
      if (startPeriod.toUpperCase() === 'PM' && startHour !== 12) startHour += 12;
      if (startPeriod.toUpperCase() === 'AM' && startHour === 12) startHour = 0;
    }
    
    if (endPeriod) {
      if (endPeriod.toUpperCase() === 'PM' && endHour !== 12) endHour += 12;
      if (endPeriod.toUpperCase() === 'AM' && endHour === 12) endHour = 0;
    }
    
    return {
      start: `${startHour.toString().padStart(2, '0')}:${startMin}`,
      end: `${endHour.toString().padStart(2, '0')}:${endMin}`
    };
  };

  const generateCalendarDays = () => {
    const days: { date: Date; isVisitDay: boolean; notes?: string; time?: string }[] = [];
    const current = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);

    while (current <= end) {
      const dateStr = toLocalDateString(current);
      const scheduledDay = visitSchedule.find(
        (vs) => toLocalDateString(vs.date) === dateStr
      );

      days.push({
        date: new Date(current),
        isVisitDay: scheduledDay?.isVisitDay ?? true,
        notes: scheduledDay?.notes,
        time: scheduledDay?.time
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const today = getTodayLocal();

  // Get current time considering today's override if exists
  const getCurrentDisplayTime = () => {
    const todayStr = toLocalDateString(today);
    const todaySchedule = visitSchedule.find(vs => toLocalDateString(vs.date) === todayStr);
    return todaySchedule?.time || currentPreferredTime || 'Not set';
  };

  const handleCancelDay = async (date: Date) => {
    if (date < today) {
      alert('Cannot cancel past days');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to cancel the visit on ${date.toLocaleDateString()}? The caregiver will be notified.`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      // Send date as YYYY-MM-DD string in local timezone
      const dateStr = toLocalDateString(date);
      const response = await axios.post('/api/jobs/update-visit-schedule', {
        jobId,
        date: dateStr,
        isVisitDay: false,
        caregiverId
      });

      if (response.data.success) {
        setEditingDay(null);
        if (onScheduleUpdate) {
          await onScheduleUpdate();
        }
        alert('Day cancelled successfully. Caregiver has been notified.');
      }
    } catch (error: any) {
      console.error('Error cancelling day:', error);
      alert(error.response?.data?.message || 'Failed to cancel day. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDay = async (date: Date) => {
    if (date < today) {
      alert('Cannot restore past days');
      return;
    }

    setLoading(true);
    try {
      // Send date as YYYY-MM-DD string in local timezone
      const dateStr = toLocalDateString(date);
      const response = await axios.post('/api/jobs/update-visit-schedule', {
        jobId,
        date: dateStr,
        isVisitDay: true,
        caregiverId
      });

      if (response.data.success) {
        setEditingDay(null);
        if (onScheduleUpdate) {
          await onScheduleUpdate();
        }
        alert('Day restored successfully. Caregiver has been notified.');
      }
    } catch (error: any) {
      console.error('Error restoring day:', error);
      alert(error.response?.data?.message || 'Failed to restore day. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async (date: Date) => {
    if (date < today) {
      alert('Cannot edit notes for past days');
      return;
    }

    setLoading(true);
    try {
      // Send date as YYYY-MM-DD string in local timezone
      const dateStr = toLocalDateString(date);
      const response = await axios.post('/api/jobs/update-visit-schedule', {
        jobId,
        date: dateStr,
        notes: noteInput,
        caregiverId
      });

      if (response.data.success) {
        setEditingDay(null);
        setNoteInput('');
        if (onScheduleUpdate) {
          await onScheduleUpdate();
        }
        alert('Note saved successfully. Caregiver has been notified.');
      }
    } catch (error: any) {
      console.error('Error saving note:', error);
      alert(error.response?.data?.message || 'Failed to save note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDayTime = async (date: Date) => {
    if (!validateTimeDuration(dayStartTime, dayEndTime)) {
      return;
    }

    setLoading(true);
    try {
      const dateStr = toLocalDateString(date);
      const timeRange = formatTimeRange(dayStartTime, dayEndTime);
      const response = await axios.put(`/api/jobs/${jobId}`, {
        'schedule.preferredTime': timeRange,
        updateDate: dateStr
      });

      if (response.data.success) {
        setDayStartTime('');
        setDayEndTime('');
        setTimeError('');
        if (onScheduleUpdate) {
          await onScheduleUpdate();
        }
        alert('Visit time updated for this day!');
      }
    } catch (error: any) {
      console.error('Error updating day time:', error);
      alert(error.response?.data?.message || 'Failed to update time');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTime = async () => {
    if (!validateTimeDuration(headerStartTime, headerEndTime)) {
      return;
    }

    setLoading(true);
    try {
      const timeRange = formatTimeRange(headerStartTime, headerEndTime);
      const payload: any = {
        'schedule.preferredTime': timeRange
      };

      if (updateAllDays) {
        payload.updateAllDays = true;
        payload.clearPerDayOverrides = true;
      } else {
        payload.updateDate = getTodayLocal().toISOString().split('T')[0];
      }

      const response = await axios.put(`/api/jobs/${jobId}`, payload);

      if (response.data.success) {
        setEditingTime(false);
        setUpdateAllDays(false);
        setTimeError('');
        if (onScheduleUpdate) {
          await onScheduleUpdate();
        }
        alert(updateAllDays ? 'Visit time updated for all days!' : 'Visit time updated successfully!');
      }
    } catch (error: any) {
      console.error('Error updating time:', error);
      alert(error.response?.data?.message || 'Failed to update time');
    } finally {
      setLoading(false);
    }
  };

  const getDayClass = (day: { date: Date; isVisitDay: boolean }) => {
    const isPast = day.date < today;
    const isToday = day.date.getTime() === today.getTime();

    if (isToday && day.isVisitDay) {
      return 'bg-primary-600 text-white font-bold shadow-md';
    }
    if (isToday && !day.isVisitDay) {
      return 'bg-gray-600 text-white font-bold opacity-75';
    }
    if (isPast && day.isVisitDay) {
      return 'bg-primary-50 text-primary-800 opacity-60';
    }
    if (isPast && !day.isVisitDay) {
      return 'bg-gray-50 text-gray-400 opacity-50';
    }
    if (day.isVisitDay) {
      return 'bg-primary-50 text-primary-900 border border-primary-100 hover:border-primary-300 hover:shadow-sm';
    }
    return 'bg-gray-50 text-gray-500 border border-gray-100 hover:border-gray-300';
  };

  const getCurrentMonthDays = () => {
    return calendarDays.filter(day => {
      return day.date.getMonth() === currentMonth.getMonth() && 
             day.date.getFullYear() === currentMonth.getFullYear();
    });
  };

  const groupCurrentMonthByWeeks = () => {
    const monthDays = getCurrentMonthDays();
    if (monthDays.length === 0) return [];

    const weeks: (typeof calendarDays[0] | null)[][] = [];
    const firstDay = monthDays[0].date;
    const startDayOfWeek = firstDay.getDay();
    
    let week: (typeof calendarDays[0] | null)[] = new Array(startDayOfWeek).fill(null);
    
    monthDays.forEach((day, index) => {
      week.push(day);
      if (day.date.getDay() === 6 || index === monthDays.length - 1) {
        while (week.length < 7) {
          week.push(null);
        }
        weeks.push([...week]);
        week = [];
      }
    });

    return weeks;
  };

  const weeks = groupCurrentMonthByWeeks();

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const canGoPrevious = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const start = new Date(startDate);
    
    const pDate = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    const sDate = new Date(start.getFullYear(), start.getMonth(), 1);
    
    return pDate >= sDate;
  };

  const canGoNext = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const end = new Date(endDate);
    
    const nDate = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
    const eDate = new Date(end.getFullYear(), end.getMonth(), 1);
    
    return nDate <= eDate;
  };

  return (
    <div className="bg-white rounded-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Visit Schedule
          </h2>
          <div className="flex gap-4 mt-1 text-sm text-gray-500">
             <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {calendarDays.length} Days
             </span>
             <span className="flex items-center gap-1">
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                 {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
             </span>
          </div>
        </div>
        
        {/* Visit Time Editor */}
        <div className="bg-gray-50 rounded-lg p-2">
          {editingTime ? (
            <div className="flex flex-col gap-2 p-2 bg-white rounded border border-gray-200 shadow-sm relative z-20">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                    <label className="text-[10px] uppercase text-gray-500 font-bold">Start</label>
                    <input
                      type="time"
                      value={headerStartTime}
                      onChange={(e) => setHeaderStartTime(e.target.value)}
                      className="px-2 py-1 text-xs border rounded bg-gray-50"
                      disabled={loading}
                    />
                </div>
                 <div className="flex flex-col">
                    <label className="text-[10px] uppercase text-gray-500 font-bold">End</label>
                    <input
                      type="time"
                      value={headerEndTime}
                      onChange={(e) => setHeaderEndTime(e.target.value)}
                      className="px-2 py-1 text-xs border rounded bg-gray-50"
                      disabled={loading}
                    />
                </div>
              </div>
              
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={updateAllDays}
                  onChange={(e) => setUpdateAllDays(e.target.checked)}
                   className="rounded text-primary-600 focus:ring-primary-500"
                />
                <span>Apply to all days</span>
              </label>

              <div className="flex gap-2 justify-end mt-1">
                 <button 
                      onClick={handleUpdateTime} 
                      disabled={loading}
                      className="text-xs bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700 font-medium"
                    >
                      Save
                  </button>
                  <button 
                      onClick={() => {
                        setEditingTime(false);
                        setUpdateAllDays(false);
                        setTimeError('');
                      }} 
                      disabled={loading}
                      className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300 font-medium"
                    >
                      Cancel
                  </button>
              </div>
              {timeError && <p className="text-red-500 text-xs">{timeError}</p>}
            </div>
          ) : (
            <div className="flex items-center gap-3">
               <div>
                  <p className="text-[10px] uppercase text-gray-400 font-bold">Preferred Time</p>
                  <p className="text-sm font-bold text-gray-900">{getCurrentDisplayTime()}</p>
               </div>
              <button 
                onClick={() => {
                  setEditingTime(true);
                  const displayTime = getCurrentDisplayTime();
                  const parsed = parseTimeRange(displayTime);
                  if (parsed) {
                    setHeaderStartTime(parsed.start);
                    setHeaderEndTime(parsed.end);
                  } else {
                    setHeaderStartTime('09:00');
                    setHeaderEndTime('17:00');
                  }
                }}
                className="text-xs text-primary-600 font-bold hover:bg-primary-50 px-2 py-1 rounded border border-transparent hover:border-primary-100 transition-colors"
              >
                EDIT
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* Special Instructions */}
        {specialInstructions && (
          <div className="mb-6 bg-yellow-50 border border-yellow-100 p-4 rounded-xl flex items-start gap-3">
            <div className="bg-yellow-100 rounded-full p-2 text-yellow-600 flex-shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
               <h4 className="text-sm font-bold text-yellow-900 mb-1">Special Instructions</h4>
               <p className="text-sm text-yellow-800">{specialInstructions}</p>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="mb-6 flex items-start gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
             <div className="text-primary-500 mt-0.5">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
             </div>
             <div>
                <p className="text-sm text-gray-900 font-medium">Click on any future day to reschedule or add notes.</p>
                <p className="text-xs text-gray-500 mt-1">The caregiver will be automatically notified of any changes.</p>
             </div>
        </div>

        {/* Legend */}
        <div className="mb-6 flex flex-wrap gap-4 text-xs text-gray-600 justify-center">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-600 rounded shadow-sm"></div>
            <span>Today</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-50 border border-primary-200 rounded"></div>
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-50 border border-gray-200 rounded"></div>
            <span>Cancelled</span>
          </div>
           <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
            <span>Has Notes</span>
          </div>
        </div>

        {/* Month Navigation */}
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={goToPreviousMonth}
            disabled={!canGoPrevious()}
            className={`p-2 rounded-lg transition-all ${
              canGoPrevious() 
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' 
                : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <h3 className="text-xl font-bold text-gray-900">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          
          <button
            onClick={goToNextMonth}
            disabled={!canGoNext()}
            className={`p-2 rounded-lg transition-all ${
              canGoNext() 
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' 
                : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day of Week Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <div key={i} className="text-center text-xs font-bold text-gray-400 uppercase tracking-wide py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="space-y-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-1">
              {week.map((day, dayIndex) => {
                if (!day) {
                  return <div key={`empty-${dayIndex}`} className="aspect-square"></div>;
                }
                
                const dateStr = day.date.toISOString().split('T')[0];
                const isEditing = editingDay === dateStr;
                const canEdit = day.date >= today;

                return (
                  <div key={dayIndex} className="relative">
                    <div
                      className={`
                        ${getDayClass(day)}
                        rounded-lg p-1 md:p-2 text-center transition-all duration-200 aspect-square flex flex-col items-center justify-center
                        ${canEdit ? 'cursor-pointer' : ''}
                        relative group
                      `}
                      onClick={() => {
                        if (canEdit) {
                          setEditingDay(dateStr);
                          setNoteInput(day.notes || '');
                          setDayStartTime('');
                          setDayEndTime('');
                          setTimeError('');
                        }
                      }}
                    >
                      
                      <div className="text-sm md:text-base font-bold">
                        {day.date.getDate()}
                      </div>
                      
                      {day.notes && (
                        <div className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></div>
                      )}
                    </div>

                    {/* Edit Modal */}
                    {isEditing && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingDay(null)}>
                        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-between items-start mb-4">
                             <h3 className="text-xl font-bold text-gray-900">
                                {day.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                             </h3>
                             <button onClick={() => setEditingDay(null)} className="text-gray-400 hover:text-gray-500">
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                             </button>
                          </div>

                          <div className="mb-5">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Visit Time</label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 mb-3">
                                <p className="text-sm font-medium text-gray-900">Current: {day.time || currentPreferredTime || 'Not set'}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 mb-2">
                              <div>
                                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">New Start</label>
                                <input
                                  type="time"
                                  value={dayStartTime}
                                  onChange={(e) => setDayStartTime(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white"
                                  disabled={loading}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">New End</label>
                                <input
                                  type="time"
                                  value={dayEndTime}
                                  onChange={(e) => setDayEndTime(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white"
                                  disabled={loading}
                                />
                              </div>
                            </div>
                            {timeError && <p className="text-red-600 text-xs mb-2">{timeError}</p>}
                            {(dayStartTime || dayEndTime) && (
                              <button
                                onClick={() => handleSaveDayTime(day.date)}
                                disabled={loading}
                                className="w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-bold text-sm shadow-sm shadow-primary-200"
                              >
                                {loading ? 'Updating...' : 'Update Time'}
                              </button>
                            )}
                          </div>

                          <div className="mb-5">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</label>
                            <textarea
                              value={noteInput}
                              onChange={(e) => setNoteInput(e.target.value)}
                              placeholder="Add instructions for the caregiver..."
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors"
                              rows={3}
                              disabled={loading}
                            />
                             <button
                              onClick={() => handleSaveNote(day.date)}
                              disabled={loading}
                              className="mt-2 text-xs font-bold text-primary-600 hover:text-primary-700 hover:underline"
                            >
                              {loading ? 'Saving...' : 'Save Note'}
                            </button>
                          </div>

                          <div className="pt-4 border-t border-gray-100">
                             <p className="text-xs text-center text-gray-400 mb-3 uppercase font-bold tracking-wider">Status Actions</p>
                            {day.isVisitDay ? (
                              <button
                                onClick={() => handleCancelDay(day.date)}
                                disabled={loading}
                                className="w-full py-3 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 font-bold flex items-center justify-center gap-2 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancel Visit
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRestoreDay(day.date)}
                                disabled={loading}
                                className="w-full py-3 bg-green-50 text-green-700 border border-green-200 rounded-xl hover:bg-green-100 font-bold flex items-center justify-center gap-2 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Restore Visit
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer Stats - Simplified */}
        <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex justify-center gap-8 text-center">
                 <div>
                    <p className="text-2xl font-bold text-gray-900">{calendarDays.filter(d => d.isVisitDay).length}</p>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Scheduled</p>
                 </div>
                 <div>
                    <p className="text-2xl font-bold text-gray-400">{calendarDays.filter(d => !d.isVisitDay).length}</p>
                     <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Cancelled</p>
                 </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerVisitCalendar;
